import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  Settings,
  ListChecks,
  Building2,
  FileText,
  MessageSquare,
  BarChart3,
} from "lucide-react";

interface AdminSidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  userRole: string;
}

const AdminSidebar = ({ activeTab, setActiveTab, userRole }: AdminSidebarProps) => {
  const isAdmin = userRole === 'admin';
  const isEditor = userRole === 'editor';
  const canManageContent = isAdmin || isEditor;
  
  return (
    <aside className="h-screen w-64 border-r border-gray-200 bg-white">
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <h3 className="font-medium text-lg">Admin Dashboard</h3>
          <p className="text-sm text-gray-500 capitalize">{userRole} Role</p>
        </div>
        
        <div className="flex-grow overflow-auto py-2">
          <nav className="px-2 space-y-1">
            <Link 
              to="/admin"
              className={cn(
                "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                activeTab === "dashboard" ? "bg-gray-100 font-medium" : "text-gray-600"
              )}
              onClick={() => setActiveTab("dashboard")}
            >
              <LayoutDashboard className="mr-3 h-5 w-5" />
              Dashboard
            </Link>
            
            {isAdmin && (
              <>
                <Link 
                  to="/admin"
                  state={{ activeTab: 'providers' }}
                  className={cn(
                    "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                    activeTab === "providers" ? "bg-gray-100 font-medium" : "text-gray-600"
                  )}
                  onClick={() => setActiveTab("providers")}
                >
                  <Building2 className="mr-3 h-5 w-5" />
                  Providers
                </Link>

                <Link 
                  to="/admin"
                  state={{ activeTab: 'users' }}
                  className={cn(
                    "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                    activeTab === "users" ? "bg-gray-100 font-medium" : "text-gray-600"
                  )}
                  onClick={() => setActiveTab("users")}
                >
                  <Users className="mr-3 h-5 w-5" />
                  Users
                </Link>
              </>
            )}
            
            {!isEditor && (
              <>
                <Link 
                  to="/admin"
                  state={{ activeTab: 'lead-accounts' }}
                  className={cn(
                    "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                    activeTab === "lead-accounts" ? "bg-gray-100 font-medium" : "text-gray-600"
                  )}
                  onClick={() => setActiveTab("lead-accounts")}
                >
                  <ListChecks className="mr-3 h-5 w-5" />
                  Lead Accounts
                </Link>

                <Link 
                  to="/admin"
                  state={{ activeTab: 'strategies' }}
                  className={cn(
                    "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                    activeTab === "strategies" ? "bg-gray-100 font-medium" : "text-gray-600"
                  )}
                  onClick={() => setActiveTab("strategies")}
                >
                  <BarChart3 className="mr-3 h-5 w-5" />
                  Strategies
                </Link>

                <Link 
                  to="/admin"
                  state={{ activeTab: 'accounts' }}
                  className={cn(
                    "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                    activeTab === "accounts" ? "bg-gray-100 font-medium" : "text-gray-600"
                  )}
                  onClick={() => setActiveTab("accounts")}
                >
                  <Settings className="mr-3 h-5 w-5" />
                  Accounts
                </Link>
              </>
            )}
            
            {canManageContent && (
              <>
                <Link 
                  to="/admin"
                  state={{ activeTab: 'articles' }}
                  className={cn(
                    "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                    activeTab === "articles" ? "bg-gray-100 font-medium" : "text-gray-600"
                  )}
                  onClick={() => setActiveTab("articles")}
                >
                  <FileText className="mr-3 h-5 w-5" />
                  Articles
                </Link>
                
                <Link 
                  to="/admin"
                  state={{ activeTab: 'authors' }}
                  className={cn(
                    "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                    activeTab === "authors" ? "bg-gray-100 font-medium" : "text-gray-600"
                  )}
                  onClick={() => setActiveTab("authors")}
                >
                  <MessageSquare className="mr-3 h-5 w-5" />
                  Authors
                </Link>
                
                <Link 
                  to="/admin"
                  state={{ activeTab: 'faqs' }}
                  className={cn(
                    "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                    activeTab === "faqs" ? "bg-gray-100 font-medium" : "text-gray-600"
                  )}
                  onClick={() => setActiveTab("faqs")}
                >
                  <MessageSquare className="mr-3 h-5 w-5" />
                  FAQs
                </Link>
              </>
            )}
            
            {/* Add Monitoring Dashboard Link */}
            <Link 
              to="/admin/monitoring"
              className={cn(
                "flex items-center text-sm px-3 py-2 rounded-md hover:bg-gray-100",
                activeTab === "monitoring" ? "bg-gray-100 font-medium" : "text-gray-600"
              )}
              onClick={() => {}}
            >
              <BarChart3 className="mr-3 h-5 w-5" />
              Monitoring Dashboard
            </Link>
          </nav>
        </div>
        
        <div className="p-4 border-t">
          <p className="text-sm text-gray-500">Footer content here</p>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
